import { Check } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/StatusBadge';
import { formatAgo } from '@/lib/ago';
import { graceLabel, switchStepIndex, SWITCH_STEP_LABELS, SWITCH_STEPS } from '@/lib/domainSwitch';
import { cn } from '@/lib/utils';
import type { DomainSwitch } from '@/types/domain';

function When({ iso, now }: { iso: string | null; now: number | undefined }) {
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return <>—</>;
  return (
    <time dateTime={new Date(t).toISOString()} title={new Date(t).toLocaleString()}>
      {t > (now ?? 0) ? new Date(t).toLocaleString() : formatAgo(t, now)}
    </time>
  );
}

const STATUS_LABEL: Record<DomainSwitch['status'], string> = {
  in_progress: 'In progress',
  verifying: 'Verifying',
  awaiting_cleanup: 'Grace period',
  done: 'Done',
  failed: 'Failed',
};

/** The app's latest domain change: a step list, both hostnames and the grace period or the failure. */
export function DomainSwitchStatus({ sw, now }: { sw: DomainSwitch; now: number | undefined }) {
  const current = switchStepIndex(sw.status);
  const failed = sw.status === 'failed';

  return (
    <Card className="min-w-0 gap-3 p-4 md:col-span-2" aria-labelledby={`switch-${sw.id}-title`} role="region">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id={`switch-${sw.id}-title`} className="text-base font-semibold">
          Domain change
        </h2>
        <StatusBadge status={sw.status} label={STATUS_LABEL[sw.status]} />
      </div>

      {failed ? null : (
        <ol className="grid gap-2 sm:grid-cols-4" aria-label="Steps">
          {SWITCH_STEPS.map((step, i) => {
            // "done" is a terminal state: every step is complete then.
            const complete = i < current || sw.status === 'done';
            const active = i === current && sw.status !== 'done';
            return (
              <li
                key={step}
                aria-current={active ? 'step' : undefined}
                className={cn(
                  'flex items-center gap-2 rounded-md border px-3 py-2 text-sm',
                  active && 'border-primary bg-accent font-medium',
                  !complete && !active && 'text-muted-foreground',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'flex size-5 shrink-0 items-center justify-center rounded-full border text-xs tabular-nums',
                    complete && 'border-primary bg-primary text-primary-foreground',
                    active && 'border-primary text-primary',
                  )}
                >
                  {complete ? <Check className="size-3" /> : i + 1}
                </span>
                <span>{SWITCH_STEP_LABELS[step]}</span>
                <span className="sr-only">{complete ? '(done)' : active ? '(current)' : '(pending)'}</span>
              </li>
            );
          })}
        </ol>
      )}

      <dl className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-[max-content_1fr]">
        <dt className="text-muted-foreground">From</dt>
        <dd className="font-mono text-xs [overflow-wrap:anywhere]">{sw.from_host}</dd>
        <dt className="text-muted-foreground">To</dt>
        <dd className="font-mono text-xs [overflow-wrap:anywhere]">{sw.to_host}</dd>
        <dt className="text-muted-foreground">Grace period</dt>
        <dd>
          {graceLabel(sw.grace_period_minutes)}
          {sw.grace_until && sw.status === 'awaiting_cleanup' ? (
            <span className="text-muted-foreground">
              {' '}
              (old hostname removed after <When iso={sw.grace_until} now={now} />)
            </span>
          ) : null}
        </dd>
        <dt className="text-muted-foreground">Started</dt>
        <dd>
          <When iso={sw.created_at} now={now} />
        </dd>
        {sw.completed_at ? (
          <>
            <dt className="text-muted-foreground">{failed ? 'Failed' : 'Finished'}</dt>
            <dd>
              <When iso={sw.completed_at} now={now} />
            </dd>
          </>
        ) : null}
      </dl>

      {failed ? (
        <p className="rounded-md bg-status-danger-soft p-3 text-sm text-status-danger [overflow-wrap:anywhere]">
          The change was rolled back; the app still runs on <span className="font-mono">{sw.from_host}</span>.
          {sw.error ? <span className="mt-1 block font-mono text-xs">{sw.error}</span> : null}
        </p>
      ) : null}
    </Card>
  );
}
